import { ChangeDetectionStrategy, Component, Inject, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BranchesService } from '@portal-core/branches/services/branches.service';
import { ChecklistTemplateColumn } from '@portal-core/checklist-templates/models/checklist-template-column.model';
import { ChecklistTemplateItem } from '@portal-core/checklist-templates/models/checklist-template-item.model';
import { ChecklistTemplate } from '@portal-core/checklist-templates/models/checklist-template.model';
import { ChecklistTemplatesService } from '@portal-core/checklist-templates/services/checklist-templates.service';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { ProjectChecklistColumnsFormComponent } from '@portal-core/project-checklists/components/project-checklist-columns-form/project-checklist-columns-form.component';
import { ProjectChecklistRowsFormComponent } from '@portal-core/project-checklists/components/project-checklist-rows-form/project-checklist-rows-form.component';
import { ProjectChecklistSettingsForm, ProjectChecklistSettingsFormComponent } from '@portal-core/project-checklists/components/project-checklist-settings-form/project-checklist-settings-form.component';
import { ProjectChecklistType } from '@portal-core/project-checklists/enums/project-checklist-type.enum';
import { ProjectChecklist } from '@portal-core/project-checklists/models/project-checklist.model';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { SnackBarAutoDismissDurationMS } from '@portal-core/ui/snack-bar/constants/snack-bar-auto-dismiss-duration.constant';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { LoadingState } from '@portal-core/util/loading-state';

export interface ChecklistTemplateCreateForm {
  settingsForm: FormGroup<ProjectChecklistSettingsForm>;
};

export interface ChecklistTemplateCreateDialogData {
  licenseId: number;
  checklist?: ProjectChecklist;
  template?: ChecklistTemplate;
};

export interface ChecklistTemplateCreateDialogResult {
  newChecklistTemplateId: number;
}

@Component({
  selector: 'mc-checklist-template-create-dialog',
  templateUrl: './checklist-template-create-dialog.component.html',
  styleUrls: ['./checklist-template-create-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
@AutoUnsubscribe()
export class ChecklistTemplateCreateDialogComponent extends DialogBase {
  static DialogConfig: MatDialogConfig = {
    width: '102rem',
    height: '67rem'
  };

  @ViewChild('columnsForm', { static: true }) projectChecklistColumnsFormComponent: ProjectChecklistColumnsFormComponent;
  @ViewChild('rowsForm', { static: false }) projectChecklistRowsFormComponent: ProjectChecklistRowsFormComponent;
  @ViewChild('settingsForm', { static: true }) projectChecklistSettingsFormComponent: ProjectChecklistSettingsFormComponent;

  ProjectChecklistType: typeof ProjectChecklistType = ProjectChecklistType;

  createChecklistTemplateForm: FormGroup<ChecklistTemplateCreateForm>;
  template: ChecklistTemplate;
  savingState: LoadingState<string> = new LoadingState<string>();

  private readonly rowsIndex: number = 2;

  get settingsStepLabel(): string {
    return this.createChecklistTemplateForm?.get('settingsForm.name')?.value;
  }

  get columns(): ChecklistTemplateColumn[] {
    return this.projectChecklistColumnsFormComponent?.getChecklistTemplateColumns() ?? [];
  }

  get items(): ChecklistTemplateItem[] {
    return this.projectChecklistRowsFormComponent?.getItems() ?? [];
  }

  get columnsStepLabel(): string {
    if (this.columns?.length === 1) {
      return this.columns.length + ' column';
    } else if (this.columns?.length > 0) {
      return this.columns.length + ' columns';
    }
  }

  get rowsStepLabel(): string {
    var checklistType = this.createChecklistTemplateForm?.get('settingsForm.type');

    if (checklistType?.value === ProjectChecklistType.ProjectFiles) {
      return 'Files';
    } else if (this.items?.length === 1) {
      return this.items.length + ' row';
    } else if (this.items?.length > 0) {
      return this.items.length + ' rows';
    }
  }

  get requirePromptOnClose(): boolean {
    return this.createChecklistTemplateForm?.dirty ?? false;
  }

  get promptDisplayText(): string {
    return 'Are you sure you want to close the wizard? All changes will be discarded.';
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ChecklistTemplateCreateDialogData,
    dialog: MatDialog,
    dialogRef: MatDialogRef<ChecklistTemplateCreateDialogData>,
    public branchesService: BranchesService,
    private checklistTemplatesService: ChecklistTemplatesService,
    private errorService: ErrorService,
    private formBuilder: UntypedFormBuilder,
    private snackBar: MatSnackBar
  ) {
    super(dialog, dialogRef);
    this.buildForm();
  }

  ngOnInit() {
    super.ngOnInit();

    // Set the template value based on the input data
    const copyFrom = this.data.template ?? this.data.checklist;

    if (copyFrom) {
      this.template = {
        Id: 0,
        Name: copyFrom.Name,
        Type: copyFrom.Type,
        Description: copyFrom.Description,
        Items: copyFrom.Type === ProjectChecklistType.GenericList ? copyFrom.Items?.map(item => {
          return {
            Id: 0,
            Name: item.Name,
            Type: item.Type,
            Order: item.Order,
            Note: item.Note
          };
        }) : null,
        Columns: copyFrom.Columns?.map(column => {
          return {
            ChecklistTemplateId: 0,
            Name: column.Name,
            Order: column.Order
          };
        })
      };
    }
  }

  ngAfterViewInit() {
    // Now that the forms have been rendered we can initialize the main form
    this.initForm();

    // If there is an initial value for the name then validate it
    if (this.createChecklistTemplateForm.get('settingsForm.name').value) {
      // Use a timeout to avoid `ExpressionChangedAfterItHasBeenCheckedError`
      setTimeout(() => {
        this.createChecklistTemplateForm.get('settingsForm.name').markAsTouched();
        this.createChecklistTemplateForm.get('settingsForm.name').updateValueAndValidity();
      }, 0);
    }
  }

  onSubmit() {
    this.savingState.update(true);

    const template: ChecklistTemplate = {
      Id: 0,
      LicenseId: this.data.licenseId,
      Name: this.createChecklistTemplateForm.get('settingsForm.name').value,
      Type: this.createChecklistTemplateForm.get('settingsForm.type').value,
      Columns: this.columns,
      Items: this.items,
      Description: this.createChecklistTemplateForm.get('settingsForm.description').value
    };

    this.checklistTemplatesService.createChecklistTemplate$(template).subscribe(newChecklistTemplate => {
      this.snackBar.open('Created new checklist template', 'OK', {
        duration: SnackBarAutoDismissDurationMS
      });
      const result: ChecklistTemplateCreateDialogResult = { newChecklistTemplateId: newChecklistTemplate?.Result?.Id };
      this.closeDialog(result, true);
    }, error => {
      this.savingState.update(false, 'Unable to create the checklist template.', this.errorService.getErrorMessages(error));
    });
  }

  private buildForm() {
    this.createChecklistTemplateForm = this.formBuilder.group({
      settingsForm: this.formBuilder.group({})
    });
  }

  private initForm() {
    this.createChecklistTemplateForm.setControl('settingsForm', this.projectChecklistSettingsFormComponent?.checklistSettingsForm);
  }
}
